// React integration for demo mode
// Provides the DemoController with React Router + Toast access
// Renders subtle "DEMO MODE" badge

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { isDemoMode } from './demoMode';
import { DemoController } from './DemoController';
import { installDemoFetchInterceptor } from './demoFetchInterceptor';
import { demoScenarios } from './demoScenarios';

// Singleton controller instance
let controllerInstance: DemoController | null = null;

function getController(): DemoController {
  if (!controllerInstance) {
    controllerInstance = new DemoController();
  }
  return controllerInstance;
}

const DemoBadge: React.FC = () => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        zIndex: 99998,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        background: 'rgba(249, 115, 22, 0.15)',
        border: '1px solid rgba(249, 115, 22, 0.3)',
        borderRadius: 8,
        color: 'rgba(249, 115, 22, 0.7)',
        fontSize: 12,
        fontFamily: 'monospace',
        fontWeight: 600,
        letterSpacing: '0.05em',
        backdropFilter: 'blur(8px)',
        transition: 'opacity 0.2s',
        opacity: hovered ? 1 : 0.4,
        cursor: 'default',
        userSelect: 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: '#f97316',
        animation: 'flowstock-demo-dot-pulse 2s ease-in-out infinite',
      }} />
      DEMO MODE
      {hovered && (
        <button
          onClick={() => getController().deactivate()}
          style={{
            marginLeft: 4,
            padding: '2px 8px',
            background: 'rgba(249, 115, 22, 0.2)',
            border: '1px solid rgba(249, 115, 22, 0.4)',
            borderRadius: 4,
            color: '#f97316',
            fontSize: 10,
            cursor: 'pointer',
            fontFamily: 'monospace',
          }}
        >
          Exit
        </button>
      )}
    </div>
  );
};

const DemoInitializer: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const controller = getController();

    // Inject React Router navigate
    controller.setNavigate(navigate);

    // Inject toast function
    controller.setShowToast((type: string, message: string) => {
      const fn = (toast as any)[type];
      if (typeof fn === 'function') fn(message);
      else toast.info(message);
    });

    // Register scenarios
    controller.registerScenarios(demoScenarios);

    // Install fetch interceptor
    installDemoFetchInterceptor();

    // Expose on window
    window.flowstockDemo = controller;

    console.log(
      '%c[Flowstock Demo Mode] %cActive — use window.flowstockDemo to control the app programmatically.',
      'color: #f97316; font-weight: bold;',
      'color: inherit;'
    );
    console.log(
      '%cAvailable scenarios:',
      'color: #f97316;',
      controller.listScenarios().join(', ')
    );
    console.log(
      '%cExample: %cawait window.flowstockDemo.runScenario("browse-catalog")',
      'color: #f97316;',
      'color: #22c55e; font-family: monospace;'
    );

    // No cleanup — window.flowstockDemo is a singleton that persists for the session.
    // Cleanup would break in React 18 StrictMode (double-mount in dev).
  }, [navigate, toast]);

  return null;
};

export const DemoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!isDemoMode()) return <>{children}</>;

  return (
    <>
      {/* Inject keyframe styles for the badge */}
      <style>{`
        @keyframes flowstock-demo-dot-pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      `}</style>
      <DemoInitializer />
      <DemoBadge />
      {children}
    </>
  );
};
